// Hook DEV-only para "saltar" un ejercicio prescrito sin hacerlo
// realmente: crea una fila en `sesiones` con estado='en_progreso' y
// la cierra inmediatamente vía la RPC `guardar_sesion_completada` con
// valores mínimos (0 reps, scores null, fps 0, duration 0, pose_analysis
// V1 vacío). Después de eso la sesión cuenta como 'completada' para
// `useEjerciciosDelDia` y el próximo ejercicio se vuelve 'actual'.
//
// SOLO se usa desde el botón "Skip (QA)" en HomePaciente, que está
// guardado por `import.meta.env.DEV` — no aparece en producción.
//
// Compone useCrearSesion + useGuardarSesionCompletada (los mismos hooks
// que usa el flow real), así si alguno cambia su shape, este hook se
// actualiza automáticamente con typecheck.

import { useState } from "react";
import { useCrearSesion } from "./useCrearSesion";
import {
  useGuardarSesionCompletada,
  type PoseAnalysisV1,
} from "./useGuardarSesionCompletada";

export interface SkipEjercicioInput {
  planEjercicioId: string;
  ejercicioId: string;
}

export interface UseSkipEjercicioReturn {
  skip: (input: SkipEjercicioInput) => Promise<boolean>;
  loading: boolean;
  error: Error | null;
}

/** Pose analysis stub para sesiones skippeadas. Cumple el shape V1 (no
 *  rompe queries del kine que branchean por `version`). Todos los valores
 *  son 0/vacío — no hay data clínica real. */
const SKIPPED_POSE_ANALYSIS: PoseAnalysisV1 = {
  version: 1,
  detector: {
    schema_id: "skipped",
    schema_version: "0",
    evaluator_version: "skipped",
  },
  captura: {
    user_agent: "skipped",
  },
  ejecucion: {
    series: [],
  },
  calidad_global: {
    violaciones_frecuentes: [],
  },
};

export function useSkipEjercicio(): UseSkipEjercicioReturn {
  const crearSesion = useCrearSesion();
  const guardarSesion = useGuardarSesionCompletada();
  const [error, setError] = useState<Error | null>(null);

  async function skip(input: SkipEjercicioInput): Promise<boolean> {
    setError(null);
    // Step 1: crear sesion en_progreso.
    const sessionId = await crearSesion.crear({
      planEjercicioId: input.planEjercicioId,
      ejercicioId: input.ejercicioId,
    });
    if (!sessionId) {
      const e = crearSesion.error ?? new Error("crearSesion no devolvió id");
      setError(e);
      return false;
    }

    // Step 2: cerrarla inmediatamente con valores mínimos. RPC valida
    // que la sesión exista y esté en estado consumible (recién creada
    // → en_progreso → OK).
    try {
      await guardarSesion.mutate({
        sesion_id: sessionId,
        total_reps: 0,
        score_promedio: null,
        score_minimo: null,
        score_maximo: null,
        fps_avg: 0,
        duration_sec: 0,
        pose_analysis: SKIPPED_POSE_ANALYSIS,
      });
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      console.error("[useSkipEjercicio] guardarSesion failed:", err);
      return false;
    }

    // guardarSesion.error queda poblado si la RPC tiró error — el hook
    // captura en try/catch interno, hay que chequear el field.
    if (guardarSesion.error) {
      setError(guardarSesion.error);
      return false;
    }

    return true;
  }

  return {
    skip,
    loading: crearSesion.loading || guardarSesion.loading,
    error,
  };
}
